/**
 * Delivery & read receipt value types and canonical codec.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/Receipt.swift`.
 *
 * Signal sends receipts as small encrypted control messages on the same
 * Double Ratchet session as user content. We keep that property: a receipt
 * is a structured plaintext payload that is encrypted and signed by the
 * E2EE layer like any other message. The server only ever sees a normal
 * E2EE envelope — it cannot observe "X read message Y", cannot move a
 * receipt for message A onto message B (messageId is in the signed
 * plaintext), and cannot demote a `read` to `delivered` (type is also in
 * the signed plaintext).
 *
 * Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
 * envelope, which is the standard kaether:e2ee:v1: form):
 *
 *   kaether:receipt:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "messageId": "abc", "type": "delivered" | "read", "ts": 1234567890 }
 *
 * The receiver runs ReceiptCodec.tryDecode on every successfully-decrypted
 * plaintext; non-receipts return null and are routed to the message
 * renderer as before.
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

export type ReceiptType = "delivered" | "read";

export interface Receipt {
  readonly schema: number;
  readonly messageId: string;
  readonly type: ReceiptType;
  /** Peer-claimed Unix epoch seconds. Stored for diagnostic use; UI should
   *  prefer ReceiptRecord.observedAt for ordering. */
  readonly ts: number;
}

export const RECEIPT_CURRENT_SCHEMA = 1;

/** Convenience constructor — mirrors iOS Receipt.init(messageId:type:ts:). */
export function makeReceipt(
  messageId: string,
  type: ReceiptType,
  ts: number = Math.floor(Date.now() / 1000),
): Receipt {
  return { schema: RECEIPT_CURRENT_SCHEMA, messageId, type, ts };
}

// ---------------------------------------------------------------------------
// Codec errors
// ---------------------------------------------------------------------------

export type ReceiptCodecErrorKind =
  | { kind: "notAReceipt" }
  | { kind: "malformed"; reason: string }
  | { kind: "unsupportedSchema"; version: number };

export class ReceiptCodecError extends Error {
  readonly tag: ReceiptCodecErrorKind;

  constructor(tag: ReceiptCodecErrorKind) {
    super(ReceiptCodecError.message(tag));
    this.name = "ReceiptCodecError";
    this.tag = tag;
  }

  private static message(tag: ReceiptCodecErrorKind): string {
    switch (tag.kind) {
      case "notAReceipt":
        return "not a receipt";
      case "malformed":
        return `malformed receipt: ${tag.reason}`;
      case "unsupportedSchema":
        return `unsupported receipt schema: ${tag.version}`;
    }
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/**
 * Sentinel prefix used for all receipt plaintexts. Mirrors iOS
 * `ReceiptCodec.prefix` exactly — same bytes, same string — so a receipt
 * encoded on web decodes on iOS and vice versa.
 */
export const RECEIPT_PREFIX = "kaether:receipt:v1:" as const;

export const ReceiptCodec = {
  prefix: RECEIPT_PREFIX,

  /** Returns true iff the plaintext looks like a receipt (fast prefix check). */
  isReceipt(plaintext: string): boolean {
    return plaintext.startsWith(RECEIPT_PREFIX);
  },

  /**
   * Encode a Receipt to the canonical wire plaintext.
   * The result can be passed directly to E2EEManager.encrypt.
   */
  encode(receipt: Receipt): string {
    const json = JSON.stringify({
      schema: receipt.schema,
      messageId: receipt.messageId,
      type: receipt.type,
      ts: receipt.ts,
    });
    return RECEIPT_PREFIX + base64UrlEncode(new TextEncoder().encode(json));
  },

  /**
   * Decode a wire plaintext produced by encode().
   *
   * Throws ReceiptCodecError:
   *   - notAReceipt   — plaintext does not start with the prefix
   *   - malformed     — base64url decode fails, JSON parse fails, or
   *                     a required field is missing/invalid
   *   - unsupportedSchema — schema field is not currentSchema (1)
   */
  decode(plaintext: string): Receipt {
    if (!plaintext.startsWith(RECEIPT_PREFIX)) {
      throw new ReceiptCodecError({ kind: "notAReceipt" });
    }
    const b64 = plaintext.slice(RECEIPT_PREFIX.length);
    let bytes: Uint8Array;
    try {
      bytes = base64UrlDecode(b64);
    } catch {
      throw new ReceiptCodecError({
        kind: "malformed",
        reason: "invalid base64url",
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parsed: any;
    try {
      parsed = JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) {
      throw new ReceiptCodecError({
        kind: "malformed",
        reason: `json parse failed: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
    if (typeof parsed.schema !== "number") {
      throw new ReceiptCodecError({
        kind: "malformed",
        reason: "missing or non-numeric schema field",
      });
    }
    if (parsed.schema !== RECEIPT_CURRENT_SCHEMA) {
      throw new ReceiptCodecError({
        kind: "unsupportedSchema",
        version: parsed.schema as number,
      });
    }
    if (typeof parsed.messageId !== "string" || parsed.messageId === "") {
      throw new ReceiptCodecError({
        kind: "malformed",
        reason: "empty messageId",
      });
    }
    if (parsed.type !== "delivered" && parsed.type !== "read") {
      throw new ReceiptCodecError({
        kind: "malformed",
        reason: `unknown receipt type: ${String(parsed.type)}`,
      });
    }
    if (typeof parsed.ts !== "number") {
      throw new ReceiptCodecError({
        kind: "malformed",
        reason: "missing or non-numeric ts field",
      });
    }
    return {
      schema: parsed.schema as number,
      messageId: parsed.messageId as string,
      type: parsed.type as ReceiptType,
      ts: parsed.ts as number,
    };
  },

  /**
   * Convenience: returns null for non-receipts. Throws only for plaintexts
   * that DO start with the prefix but are malformed — same semantics as iOS
   * `ReceiptCodec.tryDecode`.
   */
  tryDecode(plaintext: string): Receipt | null {
    if (!ReceiptCodec.isReceipt(plaintext)) return null;
    return ReceiptCodec.decode(plaintext);
  },
} as const;

// ---------------------------------------------------------------------------
// Internal helpers — mirror iOS base64url byte-for-byte
// ---------------------------------------------------------------------------

function base64UrlEncode(data: Uint8Array): string {
  // Standard base64 → base64url: + → -, / → _, strip =
  const b64 = btoa(String.fromCharCode(...data));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(s: string): Uint8Array {
  // base64url → standard base64, re-add padding
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (b64.length % 4)) % 4;
  b64 += "=".repeat(pad);
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}
